import {
  Card,
  CardContent,
  Badge,
  Button,
  cn,
  CLIENT_LOCALE,
  useFocusStore,
  navigateToThread,
  getCurrentFocus,
  navigateWithThreadPreservation,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@yeshuman/sdk'
import { Insight } from '../hooks/use-insights'
import { Edit, Trash2, Lightbulb, ExternalLink, Stethoscope, Activity, Eye } from 'lucide-react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'

interface InsightCardProps {
  insight: Insight
  onEdit?: (insight: Insight) => void
  onDelete?: (insightId: string) => void
  isHighlighted?: boolean  // For agent update animation
}

export function InsightCard({ insight, onEdit, onDelete, isHighlighted }: InsightCardProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { focus } = useFocusStore()

  const handleThreadClick = () => {
    if (insight.thread_id) {
      // If we're on /insights, stay on that route and just update the thread parameter
      if (location.pathname === '/insights') {
        const params = new URLSearchParams(searchParams)
        params.set('thread', insight.thread_id)
        navigate(`/insights?${params.toString()}`)
      } else {
        // Otherwise, navigate to the focus route with thread parameter
        const currentFocus = getCurrentFocus(focus, 'patient')
        navigateToThread(navigate, insight.thread_id, currentFocus, true, searchParams)
      }
    }
  }

  const handleRelationClick = (rel: Insight['relations'][0]) => {
    if (rel.content_type === 'observation' && rel.content_object?.id) {
      // Navigate to observations page
      navigateWithThreadPreservation(navigate, '/observations', searchParams)
    } else if (rel.content_type === 'symptom' && rel.content_object?.id) {
      // Navigate to symptoms page
      navigateWithThreadPreservation(navigate, '/symptoms', searchParams)
    } else if (rel.content_type === 'activity' && rel.content_object?.id) {
      // Navigate to activity page
      navigateWithThreadPreservation(navigate, '/activity', searchParams)
    }
  }

  const getRelationTypeLabel = (relationType: string) => {
    const labels: Record<string, string> = {
      'related_to': 'Related to',
      'context_for': 'Context for',
      'follows': 'Follows',
      'contradicts': 'Contradicts',
      'confirms': 'Confirms',
    }
    return labels[relationType] || relationType
  }

  const insightStatusVariant = (status: string) => {
    switch (status) {
      case 'emerging':
        return 'defaultSoft' as const
      case 'active':
        return 'warningSoft' as const
      case 'improving':
        return 'successSoft' as const
      case 'resolved':
        return 'secondarySoft' as const
      case 'monitoring':
        return 'secondarySoft' as const
      default:
        return 'outline' as const
    }
  }

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    return date.toLocaleDateString(CLIENT_LOCALE, { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric'
    })
  }

  const formatDateTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    const dateOnly = new Date(date)
    dateOnly.setHours(0, 0, 0, 0)
    const todayOnly = new Date(today)
    todayOnly.setHours(0, 0, 0, 0)
    const yesterdayOnly = new Date(yesterday)
    yesterdayOnly.setHours(0, 0, 0, 0)
    
    if (dateOnly.getTime() === todayOnly.getTime()) {
      return `Today at ${date.toLocaleTimeString(CLIENT_LOCALE, { hour: 'numeric', minute: '2-digit', hour12: true })}`
    } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
      return `Yesterday at ${date.toLocaleTimeString(CLIENT_LOCALE, { hour: 'numeric', minute: '2-digit', hour12: true })}`
    } else {
      return date.toLocaleString(CLIENT_LOCALE, { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })
    }
  }

  const dateRange = insight.start_date 
    ? `${formatDate(insight.start_date)}${insight.end_date ? ` - ${formatDate(insight.end_date)}` : ' - Ongoing'}`
    : null

  return (
    <Card
      className={cn(
        "transition-all duration-300 hover:shadow-md",
        isHighlighted && "ring-2 ring-primary ring-offset-2 animate-pulse",
        insight.is_exploratory && "border-dashed border-2"
      )}
    >
      <CardContent>
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="flex items-center gap-1">
                <Lightbulb className="h-3.5 w-3.5" />
                Insight
              </Badge>
              <Badge variant={insightStatusVariant(insight.status)}>
                {insight.status.charAt(0).toUpperCase() + insight.status.slice(1)}
              </Badge>
              {insight.is_exploratory && (
                <Badge variant="warningSoft">Exploratory</Badge>
              )}
              <span className="text-sm text-muted-foreground">
                Confidence: {(insight.confidence * 100).toFixed(0)}%
              </span>
              {insight.thread_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={handleThreadClick}
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Thread
                </Button>
              )}
            </div>
            
            <div>
              <h3 className="font-semibold text-base mb-1">{insight.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{insight.description}</p>
              
              {/* Date Range */}
              {dateRange && (
                <div className="mt-2 text-xs text-muted-foreground">
                  <span className="font-medium">Period:</span> {dateRange}
                </div>
              )}
              
              {/* Source Message Quote */}
              {insight.source_message_text && (
                <div className="mt-2 rounded-md border border-border/60 bg-muted/40 p-4">
                  <blockquote className="m-0 border-0 p-0 text-xs text-muted-foreground italic pl-0">
                    {insight.source_message_text}
                  </blockquote>
                </div>
              )}
              
              {/* Show relations as badges */}
              {insight.relations && insight.relations.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  <TooltipProvider>
                    {insight.relations.map((rel) => {
                      const isClickable = !!(rel.content_object?.id)
                      const relationTypeLabel = rel.relation_type !== 'related_to' 
                        ? `${getRelationTypeLabel(rel.relation_type)}: ` 
                        : ''
                      
                      const tooltipContent = (
                        <div className="space-y-1 text-xs">
                          <div className="font-semibold">{getRelationTypeLabel(rel.relation_type)}</div>
                          {rel.content_type === 'observation' && rel.content_object?.text && (
                            <div>{rel.content_object.text.substring(0, 100)}...</div>
                          )}
                          {rel.content_type === 'symptom' && rel.content_object?.severity && (
                            <div>Severity: {rel.content_object.severity}</div>
                          )}
                          {rel.content_object?.occurred_at && (
                            <div>Date: {new Date(rel.content_object.occurred_at).toLocaleDateString()}</div>
                          )}
                          {rel.content_object?.date && (
                            <div>Date: {new Date(rel.content_object.date).toLocaleDateString()}</div>
                          )}
                          <div className="text-muted-foreground">
                            {rel.discovery_method === 'agent' ? 'Auto-detected' : 
                             rel.discovery_method === 'pattern_detection' ? 'Pattern detected' :
                             'LLM analysis'}
                          </div>
                        </div>
                      )

                      const getIcon = () => {
                        if (rel.content_type === 'observation') return <Eye className="h-3.5 w-3.5" />
                        if (rel.content_type === 'symptom') return <Stethoscope className="h-3.5 w-3.5" />
                        return <Activity className="h-3.5 w-3.5" />
                      }

                      const getLabel = () => {
                        if (rel.content_type === 'observation') {
                          return rel.content_object?.text?.substring(0, 30) || 'Observation'
                        }
                        if (rel.content_type === 'symptom') {
                          return `${rel.content_object?.symptom_type || 'Unknown'}${rel.content_object?.severity ? ` (${rel.content_object.severity})` : ''}`
                        }
                        return rel.content_object?.activity_type || 'Activity'
                      }

                      return (
                        <Tooltip key={rel.id}>
                          <TooltipTrigger asChild>
                            <Badge 
                              variant="secondary" 
                              className={cn(
                                "text-xs flex items-center gap-1",
                                isClickable && "cursor-pointer hover:bg-secondary/80 transition-colors"
                              )}
                              onClick={() => isClickable && handleRelationClick(rel)}
                            >
                              {getIcon()}
                              {relationTypeLabel}
                              {getLabel()}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            {tooltipContent}
                          </TooltipContent>
                        </Tooltip>
                      )
                    })}
                  </TooltipProvider>
                </div>
              )}
            </div>
            
            <div className="text-xs text-muted-foreground">
              {formatDateTime(insight.created_at)}
            </div>
          </div>
          
          {(onEdit || onDelete) && (
            <div className="flex gap-1 ml-4">
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(insight)}
                  className="h-8 w-8 p-0"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(insight.id)}
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
